import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { useCallback, useMemo, useState } from "react";
import type { Control } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import {
  type PickerGroup,
  ToolPickerGrid,
} from "@/components/custom/tool-picker-grid/tool-picker-grid";
import { useToolkitPickerData } from "@/hooks/use-toolkit-picker-data";
import type { CatalogGrantCreateInput } from "../../types";

type ToolRefsFieldProps = {
  control: Control<CatalogGrantCreateInput>;
};

/**
 * Normalize the toolkit collection into version-less picker groups. Grants store
 * `tool_ref` in the same version-less base form gateways use for `allowed_tools`
 * (e.g. `"Gmail.SendEmail"`), so we strip any `@version` and dedupe by ref.
 */
const toGroups = (
  tools: { fully_qualified_name: string; toolkit?: { name?: string } }[]
): PickerGroup[] => {
  const byToolkit = new Map<string, Map<string, string>>();
  for (const tool of tools) {
    const ref = tool.fully_qualified_name.split("@")[0];
    const dot = ref.indexOf(".");
    const toolkit =
      tool.toolkit?.name || (dot === -1 ? ref : ref.slice(0, dot));
    const name = dot === -1 ? ref : ref.slice(dot + 1);
    const group = byToolkit.get(toolkit) ?? new Map<string, string>();
    if (!group.has(ref)) {
      group.set(ref, name);
    }
    byToolkit.set(toolkit, group);
  }
  return Array.from(byToolkit, ([toolkit, refs]) => ({
    toolkit,
    tools: Array.from(refs, ([ref, name]) => ({ ref, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    ),
  })).sort((a, b) => a.toolkit.localeCompare(b.toolkit));
};

export const ToolRefsField = ({ control }: ToolRefsFieldProps) => {
  const form = useFormContext<CatalogGrantCreateInput>();
  const { data: toolkitCollection } = useToolkitPickerData();

  const groups = useMemo(
    () => toGroups(toolkitCollection?.tools ?? []),
    [toolkitCollection]
  );

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(form.getValues("tool_refs") ?? [])
  );

  const commit = useCallback(
    (next: Set<string>) => {
      setSelected(next);
      form.setValue("tool_refs", Array.from(next), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form]
  );

  const handleToggle = useCallback(
    (ref: string, checked: boolean) => {
      const next = new Set(selected);
      if (checked) {
        next.add(ref);
      } else {
        next.delete(ref);
      }
      commit(next);
    },
    [selected, commit]
  );

  const handleToggleMany = useCallback(
    (refs: string[], checked: boolean) => {
      const next = new Set(selected);
      for (const ref of refs) {
        if (checked) {
          next.add(ref);
        } else {
          next.delete(ref);
        }
      }
      commit(next);
    },
    [selected, commit]
  );

  return (
    <FormField
      control={control}
      name="tool_refs"
      render={() => (
        <FormItem>
          <FormLabel>Tools</FormLabel>
          <FormControl>
            <ToolPickerGrid
              emptyLabel="No tools available in this project's catalog."
              groups={groups}
              onToggle={handleToggle}
              onToggleMany={handleToggleMany}
              selected={selected}
            />
          </FormControl>
          <FormDescription>
            Pick the tools this audience may self-serve.
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
